'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createEnhancedFormAction } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/helpers/createEnhancedFormAction';
import { useFormState } from 'react-dom';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { buildSteps } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/helpers/buildSteps';
import { Wizard, WizardStep } from '@app/app/_components/Wizard/Wizard';
import { useWizard } from '@app/app/_components/Wizard/useWizard';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useBeforeUnload } from '@app/lib/use-before-unload/useBeforeUnload';
import { MobileSideMenu } from '@app/app/_components/MobileSideMenu/MobileSideMenu';
import { twJoin, twMerge } from 'tailwind-merge';
import { useIsMobile } from '@app/infrastructure/application/useIsMobile';
import { MobileWizardHeader } from '@app/app/_components/MobileWizardHeader/MobileWizardHeader';
import { LifeInsuranceWizardFooter } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/LifeInsuranceWizardFooter/LifeInsuranceWizardFooter';
import { LifeCalculateResult } from '@app/domain/model/eshop/life/LifeCalculateResult';
import { recalculateLifeInsurance } from '@app/service/eshop/life/actions/recalculateLifeInsurance';
import { LifeProducts } from '@app/domain/model/eshop/life/LifeProducts';
import { Client } from '@app/domain/model/Client';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { LIFE_INSURANCE_STEPS } from '@app/app/esales/life/_sections/steps/LIFE_INSURANCE_STEPS';
import { useEditController } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/hooks/useEditController';
import { HealthQuestionsSuccess } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/HealthQuestionsSuccess';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { useDeathBeneficiaryManagement } from '@app/app/self-service/(wizard)/change-beneficiary-death/[id]/hooks/useDeathBeneficiaryManagement';
import { INITIAL_INSURED_PERSON } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/helpers/INITIAL_INSURED_PERSON';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseLifeInsuranceResult } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { GatewayRedirectStep } from '@app/app/esales/transport/_sections/steps/GatewayRedirectStep';
import { scrollToTop } from '@app/lib/scroll-to-top/scrollToTop';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';
import { useEffectOnce } from '@app/lib/use-change-effect/useEffectOnce';
import { useLifeInsuranceWizardCallbacks } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/hooks/useLifeInsuranceWizardCallbacks';
import { TenantMetadata } from '@app/infrastructure/tenant/model/TenantMetadata';
import { checkHealthEligibility } from '@app/service/eshop/life/actions/checkHealthEligibility';
import { parseEstonianPersonalCode } from '@app/lib/personal-code/parseEstonianPersonalCode';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { trackLifeFunnelStep } from '@app/infrastructure/gtm/life/trackLifeFunnelStep';

interface Props {
    orientationalPrice: number;
    callNumber: string;
    lifeProducts: LifeProducts;
    clients: Client[];
    authState: AuthState;
    sessionData: Nullable<SessionResponse>;
    tenant: TenantMetadata;
}

export function LifeInsuranceWizardSection({
    lifeProducts,
    orientationalPrice,
    callNumber,
    clients,
    authState,
    sessionData,
    tenant,
}: Props) {
    const t = useTranslations();
    const formRef = useRef<HTMLFormElement>(null);
    const visitedStepKeysRef = useRef(new Set<string>());
    const isMobile = useIsMobile();
    const parameters = useAppParameters();

    const [finished, setFinished] = useState(false);
    const [finishedMidForm, setFinishedMidForm] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const isCalculationOnlyRef = useRef(false);
    const [packagesVersion, setPackagesVersion] = useState(0);

    const resolvedPersonalCode =
        authState?.selectedClient?.personalCode || authState?.profile?.personalCode || authState?.personalCode || '';

    const initialDateOfBirth =
        parseEstonianPersonalCode(resolvedPersonalCode).birthDate ||
        parseLithuanianPersonalCode(resolvedPersonalCode).birthDate ||
        sessionData?.firstStep?.dateOfBirth ||
        '';

    const [dateOfBirth, setDateOfBirth] = useState<string>(initialDateOfBirth);

    const [formState, formAction] = useFormState(
        (prevState: FormState<PurchaseLifeInsuranceResult>, formData: FormData) =>
            enhancedFormAction(prevState, formData),
        { status: FormStatus.Pending },
    );

    const beneficiaryManagement = useDeathBeneficiaryManagement({
        selectedInsuredPerson: INITIAL_INSURED_PERSON,
        policyData: null,
        isLv: false,
        isEstonianEshop: true,
    });

    const enhancedFormAction = createEnhancedFormAction(
        authState,
        parameters.settings.general.eeEshopDefaultCountryCode ?? '',
        beneficiaryManagement,
        lifeProducts,
        isCalculationOnlyRef,
    );

    const editController = useEditController({
        selectedPackage: formState.data?.formData.selectedPackage,
    });

    const saveBeneficiary = useCallback(() => {
        beneficiaryManagement.handleSaveBeneficiary();
    }, [beneficiaryManagement]);

    const handleDateOfBirthChange = useCallback((dob: string) => {
        setDateOfBirth(dob);
    }, []);

    const handleRecalculation = useCallback(
        async (scheduleToPreview?: string): Promise<Nullable<LifeCalculateResult>> => {
            if (scheduleToPreview && formState.data) {
                const previewFormData = {
                    ...formState.data.formData,
                    paymentSchedule: scheduleToPreview,
                };

                const previewState = await recalculateLifeInsurance({
                    formData: previewFormData,
                    packages: formState.data.packages ?? [],
                    prevState: formState,
                });

                return previewState.data?.calculation ?? null;
            }

            isCalculationOnlyRef.current = true;
            formRef.current?.requestSubmit();
            return null;
        },
        [formState],
    );

    const lifeInsuranceWizardCallbacks = useLifeInsuranceWizardCallbacks();

    const selectedPackage = useMemo(
        () => formState.data?.packages?.find(pkg => pkg.code === formState.data?.formData.selectedPackage),
        [formState.data?.packages, formState.data?.formData.selectedPackage],
    );

    const steps = useMemo<WizardStep[]>(
        () =>
            buildSteps({
                translations: t,
                formState,
                handleRecalculation,
                lifeProducts,
                authState,
                editController,
                selectedPackage: selectedPackage ?? null,
                allHealthAnswersAreValid: formState.data?.allHealthAnswersAreValid ?? false,
                isTaxInformationValid: formState.data?.isTaxInformationValid ?? false,
                beneficiaryManagement,
                selectedInsuredPerson: INITIAL_INSURED_PERSON,
                sessionData,
                lifeInsuranceWizardCallbacks,
                packagesVersion,
                dateOfBirth,
                onDateOfBirthChange: handleDateOfBirthChange,
                tenant: parameters.tenant,
            }),
        [
            t,
            formState,
            handleRecalculation,
            lifeProducts,
            authState,
            editController,
            selectedPackage,
            beneficiaryManagement,
            sessionData,
            lifeInsuranceWizardCallbacks,
            packagesVersion,
            dateOfBirth,
            handleDateOfBirthChange,
            parameters.tenant,
        ],
    );

    const {
        filteredSteps,
        currentSubStepIndex,
        currentStepIndex,
        currentStepKey,
        isFirstStep,
        isFinalStep,
        next,
        back,
        setCurrentStepKey,
    } = useWizard(steps);

    useEffectOnce(() => {
        if (sessionData && sessionData.firstStep) {
            setCurrentStepKey(LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.MAIN_REASON);
        } else {
            trackLifeFunnelStep(currentStepKey);
            visitedStepKeysRef.current.add(currentStepKey);
        }
    });

    const currentStep = filteredSteps[currentStepIndex];
    const currentSubStep = currentSubStepIndex + 1;
    const totalSubSteps = currentStep?.subSteps.length || 1;

    const toTheHomepage = useCallback(() => {
        setFinishedMidForm(false);
        setFinished(false);
        setCurrentStepKey(LIFE_INSURANCE_STEPS.START);
        window.location.reload();
    }, [setCurrentStepKey]);

    const mobileHeaderSubtitle = useMemo(() => {
        if (currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HALFWAY_LOGIN) {
            return t('eshop.life.lifeStyleAndHealthLogin');
        }

        return `${filteredSteps[currentStepIndex]?.label} ${totalSubSteps !== 1 ? `${currentSubStep}/${totalSubSteps}` : ''}`;
    }, [currentStepKey, filteredSteps, currentStepIndex, totalSubSteps, currentSubStep, t]);

    useChangeEffect(() => {
        editController.setCurrentStepKeyCallback(setCurrentStepKey);
    }, [setCurrentStepKey, editController]);

    useBeforeUnload(finished, t('general.areYouSureYouWantToLeave'));

    const handleBack = useCallback(() => {
        if (isFirstStep) {
            confirm(t('general.areYouSureYouWantToLeave'));
        }

        formState.status = FormStatus.Pending;
        back();
    }, [back, formState, isFirstStep, t]);

    const onSuccessfulLogin = useCallback(() => {
        setCurrentStepKey(LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.CONTACT_DETAILS);
    }, [setCurrentStepKey]);

    useChangeEffect(() => {
        const parsedDob =
            parseEstonianPersonalCode(resolvedPersonalCode).birthDate ||
            parseLithuanianPersonalCode(resolvedPersonalCode).birthDate;

        if (!parsedDob) return;

        setDateOfBirth(parsedDob);
    }, resolvedPersonalCode);

    const handleSubmit = useCallback(() => {
        const noCalculateSteps = [
            LIFE_INSURANCE_STEPS.START,
            LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.MAIN_REASON,
            LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.AGE,
            LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.INCOME,
        ];

        const handleResetPremiumCalculation = () => {
            if (formState.data && formState.data.calculation) formState.data.calculation = undefined;
        };

        if (noCalculateSteps.includes(currentStepKey)) {
            handleResetPremiumCalculation();
        }

        isCalculationOnlyRef.current = false;
        formRef.current?.requestSubmit();
    }, [currentStepKey, formState.data]);

    const handleFinishAfterHealthQuestions = useCallback(() => {
        setFinishedMidForm(true);
    }, []);

    const handleMenuClick = useCallback(() => {
        setIsMobileMenuOpen(true);
    }, []);

    const handleMenuClose = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    useChangeEffect(async () => {
        if (formState.status === FormStatus.Success) {
            if (currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.INCOME && !isCalculationOnlyRef.current) {
                setPackagesVersion(prev => prev + 1);
            }

            if (isFinalStep && !isCalculationOnlyRef.current) {
                setFinished(true);
            } else if (!isCalculationOnlyRef.current) {
                const shouldReturn = editController.shouldReturnToSummary(
                    currentStepKey,
                    formState.data?.formData.selectedPackage,
                );

                if (shouldReturn && formState.data?.formData) {
                    const isHealthEligible = await checkHealthEligibility(
                        formState.data.formData,
                        formState.data.packages ?? [],
                        lifeProducts,
                        authState,
                    );

                    editController.resetEditMode();

                    if (!isHealthEligible) {
                        if (formState.data) {
                            formState.data.allHealthAnswersAreValid = false;
                        }
                        setCurrentStepKey(LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS_SUMMARY);
                    } else {
                        setCurrentStepKey(editController.summaryStepToReturn || LIFE_INSURANCE_STEPS.SUMMARY);
                    }
                } else if (shouldReturn) {
                    editController.resetEditMode();
                    setCurrentStepKey(editController.summaryStepToReturn || LIFE_INSURANCE_STEPS.SUMMARY);
                } else {
                    next();
                }
            }
            isCalculationOnlyRef.current = false;
        }

        if (formState.status === FormStatus.Error) {
            scrollToTop();
        }
    }, formState);

    useChangeEffect(() => {
        if (currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CHOOSE_PACKAGE) {
            isCalculationOnlyRef.current = true;
            formRef.current?.requestSubmit();
        }
    }, currentStepKey);

    useChangeEffect(() => {
        if (!visitedStepKeysRef.current.has(currentStepKey)) {
            trackLifeFunnelStep(currentStepKey);
            visitedStepKeysRef.current.add(currentStepKey);
        }
    }, currentStepKey);

    useChangeEffect(() => {
        scrollToTop();
    }, currentStepKey);

    if (finishedMidForm) {
        return <HealthQuestionsSuccess toTheHomepage={toTheHomepage} />;
    }

    if (finished) {
        return <GatewayRedirectStep checkoutResult={formState.data?.checkoutResult} />;
    }

    const finalSigningContractSteps = [
        LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.INVOICE_INFORMATION,
        LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.OVERVIEW_OF_CONTRACT,
        LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.CONSENTS,
        LIFE_INSURANCE_STEPS.PAYMENT,
    ];

    const isInFinalSigningSteps = finalSigningContractSteps.includes(currentStepKey);

    return (
        <>
            <MobileWizardHeader
                title={t('eshop.life.title')}
                subtitle={mobileHeaderSubtitle}
                currentStepIndex={currentSubStepIndex}
                totalSteps={totalSubSteps}
                onBack={
                    beneficiaryManagement.isEditingBeneficiary
                        ? beneficiaryManagement.handleCancelBeneficiaryEdit
                        : handleBack
                }
                onMenuClick={handleMenuClick}
            />

            <MobileSideMenu
                isOpen={isMobileMenuOpen}
                onClose={handleMenuClose}
                toHomePage={toTheHomepage}
                authState={authState}
                handleSubmit={handleSubmit}
                clients={clients}
                callNumber={callNumber}
                bookConsultationUrl={parameters.eshopLifeInfoUrls.bookConsultationLUrl}
                tenant={tenant}
            />

            <form
                ref={formRef}
                action={formAction}
                noValidate
                className={twJoin(
                    'max-md:pt-4',
                    (currentStepKey === LIFE_INSURANCE_STEPS.START ||
                        currentStepKey === LIFE_INSURANCE_STEPS.PACKAGES_DONE) &&
                        'max-md:h-screen max-md:bg-primary-100',
                )}>
                {!isFinalStep && <input type="hidden" name="step" value={currentStepKey} />}

                <Wizard
                    steps={filteredSteps}
                    currentStepIndex={currentStepIndex}
                    currentSubStepIndex={currentSubStepIndex}
                    onSubmit={beneficiaryManagement.isEditingBeneficiary ? saveBeneficiary : handleSubmit}
                    buttonClassName={twMerge(
                        'px-8 sm:px-14 w-fit',
                        (currentStepKey === LIFE_INSURANCE_STEPS.PACKAGES_DONE ||
                            currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HALFWAY_LOGIN ||
                            currentStepKey === LIFE_INSURANCE_STEPS.SUMMARY ||
                            currentStepKey === LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION_SUMMARY ||
                            currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS_SUMMARY ||
                            currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH_DONE) &&
                            'hidden',
                    )}
                    submitButtonLabel={isInFinalSigningSteps ? t('eshop.life.iMakeContract') : t('general.next')}
                    footerClassName="flex gap-4 w-full"
                    bodyClassName={twMerge(
                        currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HALFWAY_LOGIN && 'self-center',
                    )}
                    footer={
                        <LifeInsuranceWizardFooter
                            clients={clients}
                            authState={authState}
                            onAuthSuccess={onSuccessfulLogin}
                            formState={formState}
                            orientationalPrice={orientationalPrice}
                            callNumber={callNumber}
                            handleSubmit={handleSubmit}
                            handleFinishAfterHealthQuestions={handleFinishAfterHealthQuestions}
                            currentStepKey={currentStepKey}
                            tenant={tenant}
                        />
                    }
                    noBackButton={isMobile}
                    noIndicators={isMobile}
                    onBack={
                        beneficiaryManagement.isEditingBeneficiary
                            ? beneficiaryManagement.handleCancelBeneficiaryEdit
                            : handleBack
                    }
                    error={
                        formState.status === FormStatus.Error && formState.violations?.beneficiaries == null
                            ? t(formState.message)
                            : undefined
                    }
                />
            </form>
        </>
    );
}
